import React from 'react';
import { Users, AlertCircle, CheckCircle, Snowflake, Gift, Ticket } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr, VOUCHER_LABELS, voucherText } from '../shared';
import { fmtNum } from '../../../lib/fmt';
import { useConfirm } from '../../../components/admin/ConfirmDialog';

export default function DevToolsTab({ user, onCrystalsChange }) {
  const [crystalBalance, setCrystalBalance] = React.useState(user?.frost_crystals ?? 0);
  const [customAmount, setCustomAmount]     = React.useState('');
  const [setAmount, setSetAmount]           = React.useState('');
  const [crystalStatus, setCrystalStatus]   = React.useState(null);
  const [crystalLoading, setCrystalLoading] = React.useState(false);
  const [transferTarget, setTransferTarget] = React.useState('');
  const [transferAmount, setTransferAmount] = React.useState('');
  const [transferLoading, setTransferLoading] = React.useState(false);
  const [transferStatus, setTransferStatus] = React.useState(null);
  const [accountSuggestions, setAccountSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions]       = React.useState(false);

  // Bonos de la warband
  const [vAccount, setVAccount] = React.useState('');
  const [vInfo, setVInfo]       = React.useState(null);   // { target_username, target_game_account, vouchers }
  const [vProduct, setVProduct] = React.useState('pase_nivel_80');
  const [vAmount, setVAmount]   = React.useState('1');
  const [vLoading, setVLoading] = React.useState(false);
  const [vStatus, setVStatus]   = React.useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const lookupVouchers = async (e) => {
    e && e.preventDefault();
    if (!vAccount.trim()) return;
    setVLoading(true); setVStatus(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dev/vouchers`,
        { params: { account: vAccount.trim() }, withCredentials: true });
      setVInfo(data);
    } catch (err) {
      setVInfo(null);
      setVStatus({ ok: false, msg: apiErr(err, 'Cuenta no encontrada') });
    } finally {
      setVLoading(false);
    }
  };

  const applyVouchers = async (sign) => {
    const amount = parseInt(vAmount, 10) * sign;
    if (!vInfo || !amount) return;
    const ok = await confirm({
      title: amount > 0 ? 'Conceder bonos' : 'Retirar bonos',
      tone: amount > 0 ? 'success' : 'danger',
      confirmLabel: amount > 0 ? 'Conceder' : 'Retirar',
      body: <>{amount > 0 ? 'Conceder' : 'Retirar'} <strong>{Math.abs(amount)} × {VOUCHER_LABELS[vProduct]}</strong>{' '}
        {amount > 0 ? 'a' : 'de'} la cuenta <strong style={{ color: '#00e5ff' }}>{vInfo.target_username}</strong>.
        <br /><span style={{ color: 'rgba(224,247,255,0.5)', fontSize: 12 }}>Ahora tiene: {voucherText(vInfo.vouchers) || 'sin bonos'}</span></>,
    });
    if (!ok) return;
    setVLoading(true); setVStatus(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/dev/vouchers`,
        { target_account: vInfo.target_username, product: vProduct, amount },
        { withCredentials: true });
      setVInfo(data);
      setVStatus({ ok: true, msg: `${amount > 0 ? 'Concedidos' : 'Retirados'} ${Math.abs(amount)} × ${VOUCHER_LABELS[vProduct]} a ${data.target_username}.` });
    } catch (err) {
      setVStatus({ ok: false, msg: apiErr(err, 'Error al aplicar los bonos') });
    } finally {
      setVLoading(false);
    }
  };

  const showCrystalStatus = (ok, msg) => {
    setCrystalStatus({ ok, msg });
    setTimeout(() => setCrystalStatus(null), 3000);
  };

  const callCrystals = async (amount, action) => {
    if (action === 'set' ? !(amount >= 0) : !(amount > 0)) return;
    if (action === 'set' && !(await confirm({
      title: 'Definir balance', tone: 'danger', confirmLabel: 'Definir',
      body: <>Tu saldo pasará de <strong>{fmtNum(crystalBalance)} ❄</strong> a <strong>{fmtNum(amount)} ❄</strong>. Queda registrado en el ledger.</>,
    }))) return;
    setCrystalLoading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/dev/crystals`,
        { amount, action },
        { withCredentials: true }
      );
      setCrystalBalance(data.frost_crystals);
      onCrystalsChange && onCrystalsChange(data.frost_crystals);
      showCrystalStatus(true, `Balance actualizado: ${data.frost_crystals.toLocaleString()} ❄`);
    } catch (err) {
      showCrystalStatus(false, apiErr(err, 'Error'));
    } finally {
      setCrystalLoading(false);
    }
  };

  React.useEffect(() => {
    const term = transferTarget.trim();
    if (term.length < 2) { setAccountSuggestions([]); setShowSuggestions(false); return; }
    const t = setTimeout(() => {
      axios.get(`${BACKEND_URL}/api/dev/accounts/search`, { params: { q: term }, withCredentials: true })
        .then(({ data }) => { setAccountSuggestions(Array.isArray(data) ? data : []); setShowSuggestions(true); })
        .catch(() => setAccountSuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [transferTarget]);

  const handleTransfer = async (e) => {
    e.preventDefault();
    const amount = parseInt(transferAmount, 10);
    if (!transferTarget.trim() || !amount || amount <= 0) return;
    const ok = await confirm({
      title: 'Dar cristales a otra cuenta', tone: 'success', confirmLabel: 'Transferir',
      body: <>Se sumarán <strong style={{ color: '#00e5ff' }}>{fmtNum(amount)} ❄</strong> a la cuenta{' '}
        <strong style={{ color: '#00e5ff' }}>{transferTarget.trim()}</strong>. Queda registrado en el ledger con tu usuario.</>,
    });
    if (!ok) return;
    setTransferLoading(true);
    setTransferStatus(null);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/dev/crystals/transfer`,
        { target_account: transferTarget.trim(), amount },
        { withCredentials: true }
      );
      const ref = data.target_game_account
        ? `${data.target_username} (${data.target_game_account})`
        : data.target_username;
      setTransferStatus({
        ok: true,
        msg: `Transferidos ${data.amount_added.toLocaleString()} cristales a ${ref}. Nuevo balance: ${data.target_frost_crystals.toLocaleString()} ❄`,
      });
      setTransferAmount('');
      setTransferTarget('');
      setAccountSuggestions([]);
    } catch (err) {
      setTransferStatus({ ok: false, msg: apiErr(err, 'Error al transferir cristales') });
    } finally {
      setTransferLoading(false);
    }
  };

  const QUICK_AMOUNTS = [100, 500, 1000, 5000];

  return (
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      {confirmDialog}
      <div className="admin-section-header">
        <h2 className="admin-section-title">Herramientas de desarrollo</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 16 }}>
        {/* Cristales */}
        <div style={{ background: 'rgba(0,180,255,0.05)', border: '1px solid rgba(0,180,255,0.18)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Snowflake size={14} style={{ color: '#00e5ff' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>CRISTALES DE ESCARCHA (TEST)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ color: 'rgba(224,247,255,0.5)', fontSize: 13 }}>Balance:</span>
            <span style={{ fontWeight: 700, fontSize: 20, color: '#00e5ff' }}>{crystalBalance.toLocaleString()}</span>
            <Snowflake size={13} style={{ color: '#00e5ff' }} />
          </div>

          <p style={{ fontSize: 11, color: 'rgba(224,247,255,0.4)', marginBottom: 8 }}>Añadir rápido</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {QUICK_AMOUNTS.map((amt) => (
              <button key={amt} onClick={() => callCrystals(amt, 'add')} disabled={crystalLoading}
                style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(0,180,255,0.12)', border: '1px solid rgba(0,180,255,0.3)', color: '#00e5ff', cursor: 'pointer' }}>
                +{amt.toLocaleString()}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="number" min="1" className="form-input" style={{ flex: 1, fontSize: 13, paddingLeft: 10 }}
              placeholder="Cantidad personalizada" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} />
            <button onClick={() => { callCrystals(parseInt(customAmount), 'add'); setCustomAmount(''); }} disabled={crystalLoading || !customAmount}
              style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(0,180,255,0.12)', border: '1px solid rgba(0,180,255,0.3)', color: '#00e5ff', cursor: 'pointer' }}>
              Añadir
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <input type="number" min="0" className="form-input" style={{ flex: 1, fontSize: 13, paddingLeft: 10 }}
              placeholder="Establecer balance a..." value={setAmount} onChange={(e) => setSetAmount(e.target.value)} />
            <button onClick={() => { callCrystals(parseInt(setAmount), 'set'); setSetAmount(''); }} disabled={crystalLoading || setAmount === ''}
              style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(255,140,0,0.12)', border: '1px solid rgba(255,140,0,0.3)', color: '#ff8c00', cursor: 'pointer' }}>
              Definir
            </button>
          </div>

          {crystalStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: crystalStatus.ok ? '#4ade80' : '#f87171' }}>
              {crystalStatus.ok ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              {crystalStatus.msg}
            </div>
          )}
        </div>

        {/* Transferir (ancho completo) */}
        <div style={{ gridColumn: '1 / -1', background: 'rgba(0,180,255,0.05)', border: '1px solid rgba(0,180,255,0.18)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Gift size={14} style={{ color: '#22c55e' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>TRANSFERIR CRISTALES A OTRA CUENTA</span>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(224,247,255,0.4)', margin: '0 0 12px' }}>
            Validación por nombre de cuenta. Se excluyen cuentas RNDBOT.
          </p>
          <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }} onSubmit={handleTransfer}>
            <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
              <input
                type="text"
                className="form-input"
                style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 13, paddingLeft: 10, paddingRight: 10 }}
                placeholder="Cuenta destino (username o game_account)"
                value={transferTarget}
                onChange={(e) => { setTransferTarget(e.target.value.toUpperCase()); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => accountSuggestions.length > 0 && setShowSuggestions(true)}
                autoComplete="off"
              />
              {showSuggestions && accountSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: 'rgba(5,15,35,0.98)', border: '1px solid rgba(0,229,255,0.25)',
                  borderRadius: '0 0 8px 8px', overflow: 'hidden', marginTop: 2,
                }}>
                  {accountSuggestions.map((acc) => (
                    <button
                      key={`${acc.username}-${acc.game_account || 'none'}`}
                      type="button"
                      onMouseDown={() => {
                        setTransferTarget(acc.game_account || acc.username);
                        setShowSuggestions(false);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', padding: '9px 14px', background: 'transparent',
                        border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)',
                        cursor: 'pointer', textAlign: 'left',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,229,255,0.07)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Users size={12} style={{ color: 'rgba(0,229,255,0.5)', flexShrink: 0 }} />
                      <span style={{ color: '#e0e8f0', fontSize: 13, fontWeight: 600 }}>{acc.username}</span>
                      {acc.game_account && acc.game_account !== acc.username && (
                        <span style={{ color: 'rgba(224,247,255,0.35)', fontSize: 11 }}>· {acc.game_account}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input type="number" min="1" className="form-input" style={{ flex: 1, minWidth: 160, fontSize: 13, paddingLeft: 10 }}
              placeholder="Cantidad a transferir" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} />
            <button type="submit" disabled={transferLoading || !transferTarget.trim() || !transferAmount}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', cursor: 'pointer' }}>
              {transferLoading ? 'Enviando...' : 'Transferir'}
            </button>
          </form>
          {transferStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: transferStatus.ok ? '#4ade80' : '#f87171' }}>
              {transferStatus.ok ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              {transferStatus.msg}
            </div>
          )}
        </div>

        {/* Bonos de la warband (ancho completo) */}
        <div style={{ gridColumn: '1 / -1', background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Ticket size={14} style={{ color: '#fcd34d' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>BONOS DE LA WARBAND</span>
          </div>
          <p style={{ fontSize: 12, color: 'rgba(224,247,255,0.4)', margin: '0 0 12px' }}>
            Consulta, concede o retira bonos de una cuenta. Cada cambio queda registrado con tu usuario.
          </p>
          <form style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onSubmit={lookupVouchers}>
            <input type="text" className="form-input" autoComplete="off"
              style={{ flex: 1, minWidth: 220, fontFamily: 'monospace', fontSize: 13, paddingLeft: 10 }}
              placeholder="Cuenta (username o game_account)"
              value={vAccount} onChange={(e) => setVAccount(e.target.value.toUpperCase())} />
            <button type="submit" disabled={vLoading || !vAccount.trim()}
              style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', color: '#00e5ff', cursor: 'pointer' }}>
              Consultar
            </button>
          </form>
          {vInfo && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 12, color: '#e0e8f0' }}>
                <strong>{vInfo.target_username}</strong>
                {vInfo.target_game_account && <span style={{ color: 'rgba(224,247,255,0.4)' }}> · {vInfo.target_game_account}</span>}
                {' — '}{voucherText(vInfo.vouchers) || 'sin bonos'}
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <select className="form-input form-select" style={{ minWidth: 200, fontSize: 13, paddingLeft: 10 }}
                  value={vProduct} onChange={(e) => setVProduct(e.target.value)}>
                  {Object.entries(VOUCHER_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
                <input type="number" min="1" max="50" className="form-input" style={{ width: 90, fontSize: 13, paddingLeft: 10 }}
                  value={vAmount} onChange={(e) => setVAmount(e.target.value)} />
                <button type="button" disabled={vLoading || !(parseInt(vAmount, 10) > 0)} onClick={() => applyVouchers(1)}
                  style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', cursor: 'pointer' }}>
                  Conceder
                </button>
                <button type="button" disabled={vLoading || !(parseInt(vAmount, 10) > 0)} onClick={() => applyVouchers(-1)}
                  style={{ padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171', cursor: 'pointer' }}>
                  Retirar
                </button>
              </div>
            </div>
          )}
          {vStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: vStatus.ok ? '#4ade80' : '#f87171' }}>
              {vStatus.ok ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
              {vStatus.msg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
